// Hook para lidar com a lógica de geração e cópia de código de convite
import { useEffect, useState, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchCodeSuccess } from "../../../store/actions/_ApiCode";
import useFetch from "../../../hooks/useFetch";
import * as kApi from "../../../constants/api";
import { PROD } from "../../../constants/defaults";
import * as helpers from "../../../helpers";
import SystemContext from "../../../context/SystemContext";
import { API_Q_USERS } from "../../../constants/api";

const useInviteCodeLogic = () => {
  // Pega variáveis globais do contexto do sistema
  const { infoUsers } = useContext(SystemContext);

  // Pega a sessão do usuário do Redux
  const { session } = useSelector((state) => state.session);

  // Estado e função para buscar os usuários
  const [my_users, , , getUsers] = useFetch({
    url: API_Q_USERS,
    autoInit: false,
  });

  // Hook customizado para requisições com controle de carregamento e erro
  const [code, isFetching, code_error, fetchCode] = useFetch({
    autoInit: false, // não faz a requisição automaticamente
  });

  // Estado que guarda a quantidade de usuários ativos
  const [activeUsersCount, setActiveUsersCount] = useState(0);

  // Estados locais para controlar se os códigos foram copiados
  const [isCodeCopied, setIsCodeCopied] = useState(false);
  const [isLinkCodeCopied, setIsLinkCodeCopied] = useState(false);

  // Estados para armazenar o código e o link de convite
  const [inviteCode, setInviteCode] = useState("");
  const [inviteCodeLink, setInviteCodeLink] = useState("");

  const dispatch = useDispatch();

  // Verifica se o ambiente é produção
  const isProd = kApi.GET_PROD_OR_DEV === PROD;

  // Quantidade de convites disponíveis (do contexto)
  const invitationsAvailable = infoUsers?.usersTotal -activeUsersCount ;

  // Pega os planos disponíveis da sessão
  const plans = session?.plans;

  // Verifica se houve erro ao gerar código (ex: limite atingido)
  const hasError = code_error && code_error.status === 403 && !isFetching;

  // Verifica se o usuário está no plano gratuito
  const isFree = helpers.isFree(session?.permissions, plans?.permissions);

  // Função que dispara a busca pelos usuários
  const onGetUsers = () => {
    getUsers({
      url: API_Q_USERS,
    });
  };

  // Ao montar o componente, busca os usuários se ainda não tiver carregado
  useEffect(() => {
    if (!my_users) {
      onGetUsers();
    }
  }, []);

  // Função para contar quantos usuários estão ativos
  const activeUsers = (users) => {
    let actives = 0;
    if (users) {
      users.forEach((item) => {
        if (item.parent_activity_status === "true") {
          actives = actives + 1;
        }
      });
    }
    return actives;
  };

  // Quando `my_users` mudar, calcula a quantidade de usuários ativos
  useEffect(() => {
    if (my_users) {
      setActiveUsersCount(activeUsers(my_users));
    }
  }, [my_users]);

  // Atualiza os estados de código e link quando o código é carregado
  useEffect(() => {
    if (code && code.code) {
      const finalCode = code.code;
      setInviteCode(finalCode);
      setInviteCodeLink(kApi.STATIC_INVITE_CODE_URL + "?code=" + finalCode);
    }
  }, [code]);

  // Limpa o código do Redux ao desmontar o componente
  useEffect(() => () => dispatch(fetchCodeSuccess(null)), [dispatch]);

  // Copia o código para a área de transferência
  const copyToClipboardHandler = () => {
    const textField = document.createElement("textarea");
    textField.innerText = inviteCode;
    document.body.appendChild(textField);
    textField.select();
    document.execCommand("copy");
    textField.remove();
    setIsCodeCopied(true);
  };

  // Copia o link para a área de transferência
  const copyLinkToClipboardHandler = () => {
    const textField = document.createElement("textarea");
    textField.innerText = inviteCodeLink;
    document.body.appendChild(textField);
    textField.select();
    document.execCommand("copy");
    textField.remove();
    setIsLinkCodeCopied(true);
  };

  // Faz nova requisição para gerar código e reseta os estados de cópia
  const fetchCodeHandler = () => {
    if (hasError) return;
    setIsCodeCopied(false);
    setIsLinkCodeCopied(false);
    fetchCode({ url: kApi.API_Q_CODE });
  };

  // Retorna tudo que será usado nos componentes
  return {
    isProd,
    isCodeCopied,
    isLinkCodeCopied,
    inviteCode,
    inviteCodeLink,
    isFetching,
    code_error,
    fetchCodeHandler,
    copyToClipboardHandler,
    copyLinkToClipboardHandler,
    isFree,
    session,
    hasError,
    invitationsAvailable,
    activeUsersCount,
  };
};

export default useInviteCodeLogic;
